import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './BidModal.css';
import { useBids, useBidMessages } from '../../hooks/useBids';
import { useAuthStore } from '../../stores/authStore';
import { apiService } from '../../services/api';
import useNotificationStore from '../../stores/notificationStore';
import {
  formatPrice,
  getProductImageUrl,
  getProductName,
  isOwnStoreProduct,
} from '../../utils/helpers';
import {
  getOfferAmount,
  getOfferIdFromMessage,
  getOfferFromMessage,
  isAcceptedMessage,
  isProposalMessage,
  isBuyerMessage,
  isOfferActiveAccepted,
} from '../../utils/offerNormalize';
import { useChatAutoScroll } from '../../hooks/useChatAutoScroll';
import {
  X,
  Send,
  Loader2,
  ShoppingCart,
  Store,
  MessageCircle,
  AlertCircle,
  CheckCircle,
  XCircle,
  DollarSign,
} from 'lucide-react';

const BidModal = ({ product, isOpen, onClose }) => {
  const { t } = useTranslation(['bids', 'common']);
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const buyerId = user?.id || user?._id;

  const [bidAmount, setBidAmount] = useState('');
  const [error, setError] = useState('');
  const [processingAction, setProcessingAction] = useState(null);
  const messagesContainerRef = useRef(null);

  const productId = product?._id || product?.id;
  const ownsOwnStore = isOwnStoreProduct(product, user);

  const {
    data: thread = { messages: [], conversationId: null },
    isLoading: isLoadingMessages,
    refetch: refetchMessages,
  } = useBidMessages(isOpen ? productId : null, {
    enablePolling: isOpen,
    pollingInterval: 5000,
  });

  const messages = thread.messages ?? [];
  const conversationId = thread.conversationId;

  const { placeBidAsync, isPlacingBid, addBidToCartAsync, isAddingBidToCart, sendMessageAsync, isSendingMessage } = useBids();
  const [chatText, setChatText] = useState('');
  const showNotification = useNotificationStore((state) => state.showNotification);

  const acceptedMessage = useMemo(() => {
    const accepted = messages.filter(isAcceptedMessage);
    return accepted.length > 0 ? accepted[accepted.length - 1] : null;
  }, [messages]);

  const acceptedOffer = useMemo(
    () => getOfferFromMessage(acceptedMessage),
    [acceptedMessage]
  );

  const hasActiveAcceptedOffer = isOfferActiveAccepted(acceptedOffer);

  const pendingOfferMessage = useMemo(
    () =>
      messages.find(
        (msg) =>
          isProposalMessage(msg) &&
          (msg.offer?.status === 'PENDING' || msg.bid?.status === 'PENDING')
      ),
    [messages]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!isAuthenticated) {
      onClose?.();
      navigate('/login', { state: { from: `/product/${productId}` } });
    }
  }, [isOpen, isAuthenticated, navigate, onClose, productId]);

  const { scrollToBottom } = useChatAutoScroll({
    containerRef: messagesContainerRef,
    conversationKey: isOpen ? String(productId || '') : null,
    messageCount: messages.length,
    enabled: isOpen && !isLoadingMessages,
  });

  const handleSendText = async (e) => {
    e.preventDefault();
    const text = chatText.trim();
    if (!text || !conversationId) return;
    setError('');
    try {
      await sendMessageAsync({ conversationId, text, productId });
      setChatText('');
      await refetchMessages();
      scrollToBottom('smooth');
    } catch (err) {
      setError(err.message || t('failedToSendMessage', { defaultValue: 'Failed to send message' }));
    }
  };

  const handleSubmitBid = async (e) => {
    e.preventDefault();
    setError('');

    const amount = parseFloat(bidAmount);
    if (!bidAmount || Number.isNaN(amount) || amount <= 0) {
      setError(t('enterValidAmount'));
      return;
    }

    try {
      await placeBidAsync({ productId, bidAmount: amount, message: chatText.trim() || undefined });
      setBidAmount('');
      setChatText('');
      await refetchMessages();
      scrollToBottom('smooth');
      showNotification({
        type: 'success',
        title: t('bidPlaced', { defaultValue: 'Offer placed' }),
        message: t('bidPlacedMessage', {
          amount: formatPrice(amount),
          defaultValue: `Your offer of ${formatPrice(amount)} was submitted`,
        }),
        duration: 3000,
      });
    } catch (err) {
      setError(err.message || t('failedToPlaceBid'));
    }
  };

  const handleAddToCart = useCallback(
    async (offerId, offerData) => {
      if (!offerId) {
        setError(t('bidIdMissing', { defaultValue: 'Offer ID is missing' }));
        return;
      }
      try {
        await addBidToCartAsync({
          bidId: String(offerId),
          productId,
        });
        await refetchMessages();
        showNotification({
          type: 'success',
          title: t('addedToCartTitle', { defaultValue: 'Added to cart' }),
          message: t('bidAddedToCartMessage', {
            defaultValue: 'Accepted offer added to your cart',
          }),
          duration: 4000,
        });
      } catch (err) {
        setError(err.message || t('failedToAddToCart'));
      }
    },
    [addBidToCartAsync, productId, refetchMessages, showNotification, t]
  );

  const renderMessage = (msg, index) => {
    const isUserMessage = isBuyerMessage(msg, buyerId);
    const isAccepted = isAcceptedMessage(msg);
    const isRejected =
      msg.type === 'BID_REJECTED' ||
      msg.type === 'OFFER_REJECTED' ||
      msg.type === 'OFFER_CANCELLED';
    const isCounterOffer = msg.type === 'COUNTER_OFFER';
    const isProductInquiry = msg.type === 'PRODUCT_INQUIRY';
    const isPending = isProposalMessage(msg) && (msg.offer?.status === 'PENDING' || msg.bid?.status === 'PENDING');

    const offer = getOfferFromMessage(msg);
    const offerId = getOfferIdFromMessage(msg);
    const offerAmount = getOfferAmount(offer);

    const uniqueKey = `${msg.id || msg._id || index}-${msg.type}-${offerId || ''}`;

    const showAddToCart =
      isAccepted &&
      offerId &&
      offer?.status !== 'CONVERTED' &&
      offer?.convertedToCart !== true;

    return (
      <div key={uniqueKey} className={`bid-message ${isUserMessage ? 'user' : 'seller'}`}>
        <div
          className={`message-bubble ${isUserMessage ? 'user' : 'seller'} ${isAccepted ? 'accepted' : ''} ${isRejected ? 'rejected' : ''} ${isCounterOffer ? 'counter-offer' : ''}`}
        >
          {isProductInquiry ? (
            <div className="inquiry-content">
              <p className="inquiry-text">{msg.message}</p>
              <div className="product-preview">
                <img
                  src={getProductImageUrl(product)}
                  alt={getProductName(product)}
                  className="preview-image"
                />
                <div className="preview-info">
                  <h4>{getProductName(product)}</h4>
                  <p className="store-name">
                    <Store size={14} />
                    {product.owner?.storeName || product.owner?.name || product.brand || t('officialStore')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {(isCounterOffer || isPending) && offerAmount > 0 && (
                <div className="bid-amount-header">
                  <DollarSign size={16} />
                  <strong>
                    {isPending ? t('yourOffer') : t('counterOffer')}: {formatPrice(offerAmount)}
                  </strong>
                </div>
              )}
              <p className="message-text">{msg.message}</p>
            </>
          )}
        </div>

        {showAddToCart && (
          <div className="action-container">
            <button
              type="button"
              className="add-to-cart-btn"
              onClick={() => handleAddToCart(offerId, offer)}
              disabled={isAddingBidToCart}
            >
              {isAddingBidToCart ? (
                <>
                  <Loader2 size={18} className="spin" />
                  {t('adding')}
                </>
              ) : (
                <>
                  <ShoppingCart size={18} />
                  {t('addToCart')}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen || !product) return null;

  return (
    <div className="bid-modal-overlay" onClick={onClose}>
      <div className="bid-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bid-modal-header">
          <div className="header-info">
            <MessageCircle size={20} />
            <div>
              <h3>{t('makeOffer')}</h3>
              <p>{product.owner?.storeName || product.owner?.name || product.brand || t('officialStore')}</p>
            </div>
          </div>
          <button type="button" className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="bid-messages" ref={messagesContainerRef}>
          {isLoadingMessages ? (
            <div className="messages-loading">
              <Loader2 size={32} className="spin" />
              <p>{t('loadingConversation')}</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="messages-empty">
              <MessageCircle size={48} />
              <p>{t('startByMakingOffer', { defaultValue: 'Start by making an offer!' })}</p>
            </div>
          ) : (
            messages.map(renderMessage)
          )}
        </div>

        {error && (
          <div className="bid-error">
            <AlertCircle size={16} />
            <span>{error}</span>
            <button type="button" onClick={() => setError('')}>
              <X size={14} />
            </button>
          </div>
        )}

        {ownsOwnStore && (
          <p className="own-store-hint">{t('ownStoreOffer', { ns: 'conversations' })}</p>
        )}

        {!hasActiveAcceptedOffer && !ownsOwnStore && (
          <>
            <form className="bid-input-area bid-input-area-text" onSubmit={handleSendText}>
              <input
                type="text"
                className="text-message-input"
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                placeholder={t('typeMessage', { defaultValue: 'Type a message...' })}
                disabled={isSendingMessage || !conversationId}
              />
              <button
                type="submit"
                className="send-btn send-btn-text"
                disabled={!chatText.trim() || isSendingMessage || !conversationId}
              >
                {isSendingMessage ? <Loader2 size={20} className="spin" /> : <Send size={20} />}
              </button>
            </form>
            <form className="bid-input-area" onSubmit={handleSubmitBid}>
              {pendingOfferMessage && (
                <div className="pending-bid-notice">
                  <AlertCircle size={14} />
                  {t('pendingOfferNotice', {
                    amount: formatPrice(getOfferAmount(getOfferFromMessage(pendingOfferMessage))),
                    defaultValue: 'You have a pending offer awaiting review.',
                  })}
                </div>
              )}
              <div className="input-wrapper">
                <span className="currency-symbol">$</span>
                <input
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  placeholder={t('enterYourOffer', { defaultValue: 'Enter your offer...' })}
                  disabled={isPlacingBid}
                  step="0.01"
                  min="0"
                />
              </div>
              <button type="submit" className="send-btn send-btn-offer" disabled={!bidAmount || isPlacingBid}>
                {isPlacingBid ? <Loader2 size={20} className="spin" /> : <DollarSign size={20} />}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default BidModal;
